import { defineStore } from 'pinia'
import {
  syncAccountThemeCssVariables
} from '@/composables/useTheme'
import { getSystemThemeMode } from '@/utils/systemColorScheme'

export type ThemeMode = 'light' | 'dark'

export const useThemeStore = defineStore('theme', {
  state: () => ({
    mode: 'light' as ThemeMode,
    /** When true, `mode` tracks `prefers-color-scheme` (no host JS required; not the host's custom theme). */
    followSystemColorScheme: false,
    isInitialized: false,
    /**
     * Set when the user uses the in-app theme toggle. While true, iframe parent postMessage
     * color updates are ignored so the toggle keeps working (until URL asks for system or reload).
     */
    userOverrodeColorMode: false,
  }),
  
  getters: {
    isDark: (state) => state.mode === 'dark',
    isLight: (state) => state.mode === 'light'
  },
  
  actions: {
    syncModeClasses() {
      document.documentElement.classList.remove('theme-light', 'theme-dark')
      document.documentElement.classList.add(`theme-${this.mode}`)
      document.body.classList.remove('theme-light', 'theme-dark')
      document.body.classList.add(`theme-${this.mode}`)
    },

    applyResolvedMode(mode: ThemeMode) {
      this.mode = mode
      this.syncModeClasses()
      if (document.documentElement.hasAttribute('data-account-theme')) {
        syncAccountThemeCssVariables()
      }
    },

    setMode(mode: ThemeMode) {
      this.followSystemColorScheme = false
      this.applyResolvedMode(mode)
    },

    clearUserColorModeOverride() {
      this.userOverrodeColorMode = false
    },

    toggleMode() {
      this.userOverrodeColorMode = true
      this.followSystemColorScheme = false
      const next: ThemeMode = this.mode === 'light' ? 'dark' : 'light'
      this.applyResolvedMode(next)
    },

    enableSystemColorScheme() {
      this.followSystemColorScheme = true
      this.applyResolvedMode(getSystemThemeMode())
    },

    syncFromSystemPreference() {
      if (!this.followSystemColorScheme) return
      this.applyResolvedMode(getSystemThemeMode())
    },
    
    initializeFromUrl(mode?: string) {
      const normalized = mode?.trim().toLowerCase()
      if (normalized === 'system' || normalized === 'auto') {
        this.followSystemColorScheme = true
        this.applyResolvedMode(getSystemThemeMode())
      } else if (normalized === 'light' || normalized === 'dark') {
        this.followSystemColorScheme = false
        this.applyResolvedMode(normalized)
      } else {
        this.followSystemColorScheme = false
        this.applyResolvedMode('light')
      }
      this.isInitialized = true
    }
  }
})
